import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { visitPricingApi } from '../../api/visit-pricing.ts';
import { addOnsApi } from '../../api/add-ons.ts';
import { useLocalizedContent } from '../../i18n/localized.ts';
import type { CompanyAddOn } from '@shared/add-ons.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useOnlineStatus } from '../../hooks/index.ts';
import { useSession } from '../../state/session.tsx';
import { getDateLocale } from '../../i18n/locale.ts';
import {
  adjustedVisitPrice,
  type VisitPriceAdjustment,
  type VisitPricing,
  type VisitPricingUpdate,
} from '@shared/visit-pricing.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import { Alert, Button, TextInput, TextArea } from '../ui.tsx';
import { MoneyInput } from '../money-input.tsx';

function Editor({
  value,
  onSaved,
  onCancel,
}: {
  value: VisitPricing;
  onSaved: (value: VisitPricing) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const [base, setBase] = useState<number | null>(value.basePriceCents);
  const [lines, setLines] = useState<Array<{ label: string; amountCents: number | null; addOnId?: string }>>(
    value.adjustments,
  );
  const { company, currentLocation } = useSession();
  const localized = useLocalizedContent();
  // The saved add-ons (Settings › Pricing) this location offers, as chips.
  const addOns = useQuery({
    queryKey: ['visit-add-ons', company?.id, currentLocation?.id],
    queryFn: ({ signal }) => addOnsApi.list(false, signal),
    retry: false,
  });
  const addSaved = (addOn: CompanyAddOn) =>
    setLines((current) =>
      current.length >= 20
        ? current
        : [...current, { label: addOn.name, amountCents: addOn.defaultAmountCents, addOnId: addOn.id }],
    );
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pending = useRef<{ fingerprint: string; requestId: string } | null>(null);
  const alive = useRef(true);
  const sending = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const currency = value.currencyCode!;
  let total: number | null = null;
  try {
    if (base !== null && lines.every((line) => line.amountCents !== null)) {
      total = adjustedVisitPrice(base, lines as VisitPriceAdjustment[], currency);
    }
  } catch {
    /* A failed preview has no price to offer. */
  }
  const valid =
    total !== null &&
    lines.every((line) => line.label.trim().length > 0 && line.label.trim().length <= 160) &&
    reason.trim().length > 0 &&
    reason.trim().length <= 1000;
  const save = async () => {
    if (!online || !valid || sending.current) return;
    sending.current = true;
    setBusy(true);
    setError(null);
    const body = {
      revision: value.revision,
      expectedPriceCents: value.priceCents!,
      currencyCode: currency,
      basePriceCents: base!,
      adjustments: lines as VisitPriceAdjustment[],
      reason: reason.trim(),
    };
    const fingerprint = JSON.stringify(body);
    if (pending.current?.fingerprint !== fingerprint) pending.current = { fingerprint, requestId: crypto.randomUUID() };
    const input: VisitPricingUpdate = { ...body, requestId: pending.current.requestId };
    try {
      const saved = await visitPricingApi.update(value.appointmentId, input);
      if (alive.current) onSaved(saved);
    } catch (failure) {
      if (alive.current)
        setError(
          failure instanceof ApiRequestError && failure.code === 'CONFLICT'
            ? t('visitPricing.conflict')
            : failure instanceof ApiRequestError && failure.code === 'ADD_ON_UNAVAILABLE'
              ? t('visitPricing.addOnUnavailable')
              : failure instanceof ApiRequestError && failure.code === 'VALIDATION_ERROR'
                ? t('visitPricing.invalid')
                : t('visitPricing.failed'),
        );
    } finally {
      sending.current = false;
      if (alive.current) setBusy(false);
    }
  };
  return (
    <div className="mt-3" aria-label={t('visitPricing.editor')}>
      <p className="mb-3 text-sm">{t('visitPricing.taxHint')}</p>
      <MoneyInput
        label={t('visitPricing.base')}
        valueCents={base}
        currencyCode={currency}
        disabled={busy}
        onChangeCents={setBase}
      />
      <h4 className="mb-2 font-semibold">{t('visitPricing.linesHeading')}</h4>
      {addOns.data?.items.length ? (
        <div className="mb-3">
          <p id="visit-add-on-chips" className="mb-2 text-sm text-[color:var(--color-muted)]">
            {t('visitPricing.savedAddOns')}
          </p>
          <div role="group" aria-labelledby="visit-add-on-chips" className="flex flex-wrap gap-2">
            {addOns.data.items.map((addOn) => {
              const onVisit = lines.some((line) => line.addOnId === addOn.id);
              const otherCurrency = addOn.currencyCode !== currency;
              return (
                <Button
                  key={addOn.id}
                  variant="secondary"
                  aria-pressed={onVisit}
                  disabled={busy || onVisit || otherCurrency || lines.length >= 20}
                  title={
                    otherCurrency ? t('visitPricing.addOnOtherCurrency', { currency: addOn.currencyCode }) : undefined
                  }
                  onClick={() => addSaved(addOn)}
                >
                  {t('visitPricing.addOnChip', {
                    name: localized.text(addOn.name, addOn.nameTranslations),
                    amount: formatCents(addOn.defaultAmountCents, addOn.currencyCode, getDateLocale()),
                  })}
                </Button>
              );
            })}
          </div>
        </div>
      ) : null}
      {lines.map((line, index) => (
        <div key={index} className="mb-3 grid gap-x-3 sm:grid-cols-2">
          <TextInput
            label={t('visitPricing.label', { n: index + 1 })}
            value={line.label}
            maxLength={160}
            disabled={busy}
            onChange={(event) =>
              setLines((current) =>
                current.map((item, i) => (i === index ? { ...item, label: event.target.value } : item)),
              )
            }
          />
          <MoneyInput
            label={t('visitPricing.amount', { n: index + 1 })}
            valueCents={line.amountCents}
            currencyCode={currency}
            disabled={busy}
            onChangeCents={(amountCents) =>
              setLines((current) => current.map((item, i) => (i === index ? { ...item, amountCents } : item)))
            }
          />
          {line.addOnId ? (
            <p className="text-sm text-[color:var(--color-muted)] sm:col-span-2">{t('visitPricing.fromSavedAddOn')}</p>
          ) : null}
          <Button disabled={busy} onClick={() => setLines((current) => current.filter((_, i) => i !== index))}>
            {t('visitPricing.remove', { n: index + 1 })}
          </Button>
        </div>
      ))}
      <Button
        disabled={busy || lines.length >= 20}
        onClick={() => setLines((current) => [...current, { label: '', amountCents: null }])}
      >
        {t('visitPricing.add')}
      </Button>
      <TextArea
        label={t('visitPricing.reason')}
        value={reason}
        maxLength={1000}
        disabled={busy}
        onChange={(event) => setReason(event.target.value)}
      />
      <p className="mb-3 font-semibold" role="status">
        {t('visitPricing.total')}:{' '}
        {total === null ? t('visitPricing.invalid') : formatCents(total, currency, getDateLocale())}
      </p>
      {!online ? <Alert>{t('visitPricing.offline')}</Alert> : null}
      {error ? <Alert tone="error">{error}</Alert> : null}
      <div className="mt-3 flex flex-wrap gap-3">
        <Button variant="primary" busy={busy} disabled={!online || !valid} onClick={() => void save()}>
          {t('visitPricing.save')}
        </Button>
        <Button disabled={busy} onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
}

function ScopedVisitPriceCard({ appointmentId, onChanged }: { appointmentId: string; onChanged: () => void }) {
  const { t } = useTranslation();
  const { company, currentLocation: location, can } = useSession();
  const online = useOnlineStatus();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<VisitPricing | null>(null);
  const [tasks, setTasks] = useState<VisitPricing['checklistTasks'] | null>(null);
  const key = ['visit-pricing', company?.id, location?.id, appointmentId];
  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => visitPricingApi.get(appointmentId, signal),
    retry: false,
  });
  const value = query.data;
  return (
    <section className="card mb-3" aria-label={t('visitPricing.title')}>
      <h3 className="font-semibold">{t('visitPricing.title')}</h3>
      <p className="mt-1 text-sm text-[color:var(--color-muted)]">{t('visitPricing.scope')}</p>
      {query.isError ? (
        <Alert tone="error">
          {t('visitPricing.failed')} <Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>
        </Alert>
      ) : null}
      {query.isPending ? <p>{t('common.loading')}</p> : null}
      {value && !query.isError ? (
        <>
          <p className="mt-2">
            {t('visitPricing.total')}:{' '}
            {value.priceCents === null || value.currencyCode === null
              ? t('visitPricing.unpriced')
              : formatCents(value.priceCents, value.currencyCode, getDateLocale())}
          </p>
          {value.adjustments.length > 0 ? (
            <h4 className="mt-2 font-semibold">{t('visitPricing.linesHeading')}</h4>
          ) : null}
          {value.adjustments.length > 0 ? (
            <ul className="mt-1 list-inside list-disc">
              {value.adjustments.map((line, index) => (
                <li key={index}>
                  {line.label}: {formatCents(line.amountCents, value.currencyCode!, getDateLocale())}
                </li>
              ))}
            </ul>
          ) : null}
          {tasks?.added.length ? (
            <Alert tone="success">{t('visitPricing.tasksAdded', { tasks: tasks.added.join(', ') })}</Alert>
          ) : null}
          {tasks?.skipped.length ? (
            <Alert>{t('visitPricing.tasksSkipped', { tasks: tasks.skipped.join(', ') })}</Alert>
          ) : null}
          {value.blockedReason ? (
            <p className="mt-2 text-sm">{t(`visitPricing.blocked.${value.blockedReason}`)}</p>
          ) : null}
          {can('schedule:write') && can('payments:write') && value.editable && !editing ? (
            <Button
              disabled={!online || query.isFetching}
              onClick={() => {
                setTasks(null);
                setEditing(value);
              }}
            >
              {t('visitPricing.edit')}
            </Button>
          ) : null}
        </>
      ) : null}
      {editing ? (
        <Editor
          value={editing}
          onCancel={() => {
            setEditing(null);
            void query.refetch();
          }}
          onSaved={(saved) => {
            const { checklistTasks, ...pricing } = saved;
            setTasks(checklistTasks ?? null);
            queryClient.setQueryData(key, pricing);
            setEditing(null);
            onChanged();
          }}
        />
      ) : null}
    </section>
  );
}

export function VisitPriceCard(props: { appointmentId: string; onChanged: () => void }) {
  const { company, currentLocation: location, can } = useSession();
  return (
    <ScopedVisitPriceCard
      key={`${company?.id}:${location?.id}:${can('payments:write') && can('schedule:write')}:${props.appointmentId}`}
      {...props}
    />
  );
}
